import clsx from "clsx";
import { Fragment, type ReactNode } from "react";

import { splitEmphasisSegments } from "@/utils/highlightedText";

export type SectionHeadingAlign = "left" | "center";

export type SectionHeadingProps = {
  eyebrow?: string;
  /** Wrap highlighted phrases in `<em>` tags to render them in brand blue. */
  title?: string;
  subtitle?: string;
  align?: SectionHeadingAlign;
  className?: string;
};

function renderEmphasisText(content: string) {
  return splitEmphasisSegments(content).map((segment, index) =>
    segment.emphasized ? (
      <span key={`${segment.text}-${index}`} className="text-gail-primary">
        {segment.text}
      </span>
    ) : (
      <Fragment key={`${segment.text}-${index}`}>{segment.text}</Fragment>
    ),
  );
}

function renderHeadingContent(content: string | ReactNode): ReactNode {
  if (typeof content !== "string") return content;
  return renderEmphasisText(content);
}

export function SectionHeading({
  eyebrow,
  title,
  subtitle,
  align = "left",
  className,
}: Readonly<SectionHeadingProps>) {
  const isCentered = align === "center";

  return (
    <div className={clsx("w-full", className)}>
      {eyebrow ? (
        <p
          className={clsx(
            "font-monument text-sm uppercase tracking-[0.12em] text-gail-primary",
            isCentered && "text-center",
          )}
        >
          {renderHeadingContent(eyebrow)}
        </p>
      ) : null}
      {title ? (
        <h2
          className={clsx(
            "mt-3 font-monument text-[2rem] leading-[1.12] tracking-tight text-black md:text-[2.5rem]",
            isCentered ? "mx-auto max-w-3xl text-center" : "max-w-2xl",
          )}
        >
          {renderHeadingContent(title)}
        </h2>
      ) : null}
      {subtitle ? (
        <p
          className={clsx(
            "mt-3 font-monument text-base leading-relaxed text-black/60 md:text-lg",
            isCentered ? "mx-auto max-w-2xl text-center" : "max-w-2xl",
          )}
        >
          {renderHeadingContent(subtitle)}
        </p>
      ) : null}
    </div>
  );
}
